// client/src/pages/ClientsListPage.jsx
//
// All-clients view. Search by name/email, sort by recent. Click a row to drill
// into the client detail page. "+ New client" button uses the same
// AddClientModal that ClientPicker uses inline.
//
// Light by design — no bulk operations, no advanced filtering. Distributors
// who need bulk management probably need a real CRM, not us.

import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

export default function ClientsListPage({ getToken, navigate, showToast }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async (query = '') => {
    setLoading(true);
    try {
      const token = await getToken();
      const list = await api.withToken(token).listClients(query);
      setClients(list);
    } catch (e) {
      showToast?.('Failed to load clients: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, showToast]);

  useEffect(() => { load(''); }, [load]);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24 }}>
        <div>
          <div style={{ fontSize:22,fontWeight:700,color:'#0F172A',letterSpacing:'-0.3px' }}>Clients</div>
          <div style={{ fontSize:13,color:'#64748B',marginTop:4 }}>
            Customer records, addresses, and order history.
          </div>
        </div>
        <button onClick={() => setShowAdd(true)}
          style={{ background:G,border:'none',borderRadius:8,padding:'9px 18px',
            color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
          + New client
        </button>
      </div>

      <input value={q} onChange={e => setQ(e.target.value)}
        placeholder="Search by name or email…"
        style={{ width:'100%',border:'1px solid #E2E8F0',borderRadius:8,padding:'10px 14px',
          fontSize:13,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',
          marginBottom:16,boxSizing:'border-box' }}/>

      {loading && clients.length === 0 ? (
        <div style={{ padding:40,textAlign:'center',color:'#94A3B8',fontSize:13 }}>Loading…</div>
      ) : clients.length === 0 ? (
        <div style={{ background:'#fff',border:'1px dashed #E2E8F0',borderRadius:12,
          padding:'48px 24px',textAlign:'center' }}>
          <div style={{ fontSize:14,fontWeight:600,color:'#0F172A',marginBottom:6 }}>
            {q.trim() ? 'No clients match that search.' : 'No clients yet.'}
          </div>
          <div style={{ fontSize:12,color:'#64748B',marginBottom:14 }}>
            {q.trim()
              ? 'Try a different name or email.'
              : 'Add clients here, or create them on the fly when building a presentation.'}
          </div>
          {!q.trim() && (
            <button onClick={() => setShowAdd(true)}
              style={{ background:G,border:'none',borderRadius:7,padding:'8px 16px',
                color:'#fff',fontSize:12,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
              + Add your first client
            </button>
          )}
        </div>
      ) : (
        <div style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:10,overflow:'hidden' }}>
          <div style={{ display:'grid',gridTemplateColumns:'2fr 2fr 1.5fr 100px',gap:16,
            padding:'10px 16px',background:'#F8FAFC',
            fontSize:11,fontWeight:700,textTransform:'uppercase',
            color:'#64748B',borderBottom:'1px solid #E2E8F0',letterSpacing:'0.4px' }}>
            <div>Name</div><div>Email</div><div>Phone</div><div style={{ textAlign:'right' }}>Added</div>
          </div>
          {clients.map(c => (
            <div key={c.id} onClick={() => navigate?.('clients/' + c.id)}
              style={{ display:'grid',gridTemplateColumns:'2fr 2fr 1.5fr 100px',gap:16,
                padding:'14px 16px',fontSize:13,borderBottom:'1px solid #F1F5F9',cursor:'pointer' }}>
              <div style={{ fontWeight:500,color:'#0F172A' }}>{c.name}</div>
              <div style={{ color:'#475569' }}>{c.email || '—'}</div>
              <div style={{ color:'#475569' }}>{c.phone || '—'}</div>
              <div style={{ textAlign:'right',color:'#94A3B8',fontSize:12 }}>
                {new Date(c.created_at).toLocaleDateString('en-US', { month:'short',day:'numeric' })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddClientInlineModal
          getToken={getToken}
          onClose={() => setShowAdd(false)}
          onCreated={(c) => {
            setShowAdd(false);
            showToast?.(`Added ${c.name}`, 'success');
            navigate?.('clients/' + c.id);  // jump straight to the new client's detail page
          }}/>
      )}
    </div>
  );
}

// AddClientInlineModal — slightly larger version of the ClientPicker's
// AddClientModal that includes the optional address-and-phone expandable
// section so a client + their first address can be created in one shot.
function AddClientInlineModal({ getToken, onClose, onCreated }) {
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [showAddress, setShowAddress] = useState(false);
  const [addr, setAddr] = useState({
    label: '', ship_to_contact_name: '', ship_to_phone: '',
    line1: '', line2: '', city: '', state: '', postal_code: '', country: 'US',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Display/primary name is the business name if given, else the contact —
  // same fallback rule as ClientPicker's AddClientModal, so a client never
  // ends up with a blank display name just because only one was filled in.
  const displayName = (companyName.trim() || contactName.trim());

  const save = async () => {
    if (!displayName) { setErr('Enter a business or contact name'); return; }
    setSaving(true); setErr(null);
    try {
      const token = await getToken();
      const created = await api.withToken(token).createClient({
        name: displayName,
        company_name: companyName.trim() || null,
        contact_name: contactName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null, notes: notes.trim() || null,
      });
      // If address fields were filled, save them as the default address.
      if (showAddress && addr.line1.trim()) {
        try {
          await api.withToken(token).createClientAddress(created.id, {
            ...addr, is_default: true,
          });
        } catch { /* non-blocking */ }
      }
      onCreated(created);
    } catch (e) {
      setErr(e.message || 'Could not save');
      setSaving(false);
    }
  };

  const upd = (k, v) => setAddr(a => ({ ...a, [k]: v }));
  const inputStyle = {
    width:'100%',border:'1px solid #E2E8F0',borderRadius:7,padding:'8px 11px',
    fontSize:12,fontFamily:'inherit',outline:'none',color:'#0F172A',background:'#fff',boxSizing:'border-box',
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',zIndex:200,
      display:'flex',alignItems:'flex-start',justifyContent:'center',backdropFilter:'blur(4px)',
      padding:'4vh 24px',overflowY:'auto' }}
      onClick={e => e.target === e.currentTarget && !saving && onClose()}>
      <div style={{ background:'#fff',borderRadius:14,padding:28,maxWidth:520,width:'100%',
        boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize:18,fontWeight:600,color:'#0F172A',marginBottom:6 }}>Add a new client</div>
        <div style={{ fontSize:12,color:'#64748B',marginBottom:18,lineHeight:1.5 }}>
          You can add an address now or later. Default address auto-fills PO ship-to.
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Business name</label>
            <input value={companyName} onChange={e => setCompanyName(e.target.value)} autoFocus
              placeholder="Acme Corp" style={inputStyle}/>
          </div>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Contact name</label>
            <input value={contactName} onChange={e => setContactName(e.target.value)}
              placeholder="Jane Smith" style={inputStyle}/>
          </div>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10 }}>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email"
              placeholder="contact@acme.com" style={inputStyle}/>
          </div>
          <div>
            <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Phone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="206-555-0100" style={inputStyle}/>
          </div>
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block',fontSize:11,fontWeight:500,color:'#374151',marginBottom:4 }}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Anything you want to remember about this client…" rows={2}
            style={{ ...inputStyle, resize:'vertical', minHeight:48, fontFamily:'inherit' }}/>
        </div>

        {!showAddress ? (
          <button onClick={() => setShowAddress(true)}
            style={{ background:'none',border:'1px dashed #CBD5E1',borderRadius:7,
              padding:'10px 14px',width:'100%',fontSize:12,color:G,fontWeight:500,
              cursor:'pointer',fontFamily:'inherit',marginBottom:14 }}>
            + Add address (optional)
          </button>
        ) : (
          <div style={{ background:'#F8FAFC',border:'1px solid #E2E8F0',borderRadius:8,
            padding:14,marginBottom:14 }}>
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
              <div style={{ fontSize:12,fontWeight:600,color:'#0F172A' }}>Default address</div>
              <button onClick={() => setShowAddress(false)}
                style={{ background:'none',border:'none',color:'#94A3B8',fontSize:18,cursor:'pointer',padding:0,lineHeight:1 }}>×</button>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8 }}>
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Label</label>
                <input value={addr.label} onChange={e => upd('label', e.target.value)}
                  placeholder="HQ, Warehouse, etc." style={inputStyle}/>
              </div>
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Country</label>
                <select value={addr.country} onChange={e => upd('country', e.target.value)} style={inputStyle}>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8 }}>
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Contact name</label>
                <input value={addr.ship_to_contact_name} onChange={e => upd('ship_to_contact_name', e.target.value)}
                  placeholder="Receiving contact" style={inputStyle}/>
              </div>
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Phone</label>
                <input value={addr.ship_to_phone} onChange={e => upd('ship_to_phone', e.target.value)}
                  placeholder="For carrier" style={inputStyle}/>
              </div>
            </div>
            <div style={{ marginBottom:8 }}>
              <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Street</label>
              <input value={addr.line1} onChange={e => upd('line1', e.target.value)}
                placeholder="123 Main St" style={inputStyle}/>
            </div>
            <div style={{ marginBottom:8 }}>
              <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>Apt / suite</label>
              <input value={addr.line2} onChange={e => upd('line2', e.target.value)}
                style={inputStyle}/>
            </div>
            <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:8 }}>
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>City</label>
                <input value={addr.city} onChange={e => upd('city', e.target.value)} style={inputStyle}/>
              </div>
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>{addr.country === 'CA' ? 'Prov' : 'State'}</label>
                <input value={addr.state} onChange={e => upd('state', e.target.value)} style={inputStyle}/>
              </div>
              <div>
                <label style={{ display:'block',fontSize:10,color:'#64748B',marginBottom:3 }}>ZIP</label>
                <input value={addr.postal_code} onChange={e => upd('postal_code', e.target.value)} style={inputStyle}/>
              </div>
            </div>
          </div>
        )}

        {err && (
          <div style={{ marginBottom:12,padding:'8px 10px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:7,fontSize:11,color:'#991B1B' }}>{err}</div>
        )}
        <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={onClose} disabled={saving}
            style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:7,padding:'9px 16px',fontSize:12,color:'#475569',cursor:'pointer',fontFamily:'inherit' }}>
            Cancel
          </button>
          <button onClick={save} disabled={saving || !displayName}
            style={{ background:!displayName ? '#E2E8F0' : G,border:'none',borderRadius:7,padding:'9px 18px',fontSize:12,color:'#fff',cursor:(saving||!displayName)?'not-allowed':'pointer',fontFamily:'inherit',fontWeight:500 }}>
            {saving ? 'Saving…' : 'Create client'}
          </button>
        </div>
      </div>
    </div>
  );
}
